import { useEffect, useState } from "react";
import type { AuthStatus } from "./types.js";
import { api, isServerUnreachableError } from "../lib/api.js";
import { finishStartup, startupAuthResponse, startupError, startupHeaders, startupStage } from "../lib/startupTelemetry.js";

export function useAuthStartup(probe: number, apply: (status: AuthStatus) => void,
  clearHouseholdData: () => Promise<void>, setReady: (ready: boolean) => void,
  setUnreachable: (unreachable: boolean) => void, setError: (message: string) => void) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    let purging = false;
    const controller = new AbortController();
    setFailed(false);
    setReady(false);
    startupStage("authStartMs");
    const timer = setTimeout(() => {
      if (!active) return;
      active = false;
      controller.abort();
      setFailed(true);
      startupError("auth_timeout");
      finishStartup("error");
      // Keep the auth gate closed: a pending identity-cache purge must never
      // expose a login form before the previous household's data is cleared.
    }, 15_000);
    void api<AuthStatus>("/api/v1/auth/status", { signal: controller.signal,
      headers: startupHeaders(), onResponse: startupAuthResponse
    }).then(async (status) => {
      if (!active) return;
      if (!status.user) {
        purging = true;
        await clearHouseholdData();
        purging = false;
      }
      if (!active) return;
      setUnreachable(false);
      apply(status);
    }).catch((error: unknown) => {
      if (!active) return;
      startupError("auth_failed");
      finishStartup("error");
      if (purging) {
        // Do not reveal sign-in against a cache that may still belong to the
        // previous household. Retry repeats both the identity read and purge.
        active = false;
        setFailed(true);
      } else if (isServerUnreachableError(error)) setUnreachable(true);
      else setError(error instanceof Error ? error.message : "Could not check sign-in status");
    }).finally(() => {
      clearTimeout(timer);
      if (active) { startupStage("authReadyMs"); setReady(true); }
    });
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [probe, apply, clearHouseholdData, setReady, setUnreachable, setError]);
  return failed;
}
